// Bound canvas dimensions and raw pixel memory even with 200 measurements.
// Large legends use columns before reducing the display pixel ratio.
export const MAX_IMAGE_DIMENSION = 4096;
export const MAX_IMAGE_PIXELS = 4000000;

// Render immediately before copying: WebGL drawing buffers may be cleared
// between frames. DOM labels are painted separately at their projected positions.
export function reconstructionImage(state, legend, createCanvas = () => document.createElement('canvas')) {
  if (!state || !state.modelLoaded) throw new Error('The model is not ready.');
  const { renderer, scene, camera, root, controls, labels } = state;
  const source = renderer.domElement;
  const viewWidth = source.clientWidth, viewHeight = source.clientHeight;
  if (!viewWidth || !viewHeight || !source.width || !source.height) throw new Error('The viewer is not ready.');
  const width = legend.length > 40 ? Math.max(viewWidth, 640) : viewWidth;
  const viewScale = width / viewWidth, height = viewHeight * viewScale;
  const canvas = createCanvas();
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Image export is unavailable.');
  const font = '13px sans-serif', padding = 16, gap = 16, lineHeight = 22;
  const columns = legend.length > 40 ? Math.max(1, Math.min(4, Math.floor((width - padding * 2 + gap) / (280 + gap)))) : 1;
  const columnWidth = (width - padding * 2 - gap * (columns - 1)) / columns;
  context.font = font;
  const entries = legend.map(text => {
    const lines = [];
    let line = '';
    String(text).split(/\s+/).forEach(word => {
      const next = line ? `${line} ${word}` : word;
      if (line && context.measureText(next).width > columnWidth) {
        lines.push(line); line = word;
      } else line = next;
    });
    lines.push(line);
    return lines;
  });
  const targetRows = Math.ceil(entries.reduce((sum, lines) => sum + lines.length, 0) / columns);
  const legendColumns = Array.from({ length: columns }, () => []);
  let column = 0;
  entries.forEach(lines => {
    if (column < columns - 1 && legendColumns[column].length &&
        legendColumns[column].length + lines.length > targetRows) column += 1;
    legendColumns[column].push(...lines);
  });
  const rows = Math.max(0, ...legendColumns.map(lines => lines.length));
  const totalHeight = height + padding * 2 + rows * lineHeight;
  const ratio = Math.min(source.width / viewWidth, MAX_IMAGE_DIMENSION / width,
    MAX_IMAGE_DIMENSION / totalHeight, Math.sqrt(MAX_IMAGE_PIXELS / (width * totalHeight)));
  canvas.width = Math.max(1, Math.floor(width * ratio));
  canvas.height = Math.max(1, Math.floor(totalHeight * ratio));
  context.scale(ratio, ratio);
  context.fillStyle = '#161c25';
  context.fillRect(0, 0, width, totalHeight);
  controls.update(); root.updateMatrixWorld(true);
  renderer.render(scene, camera);
  context.save(); context.scale(viewScale, viewScale);
  context.drawImage(source, 0, 0, viewWidth, viewHeight);
  context.beginPath(); context.rect(0, 0, viewWidth, viewHeight); context.clip();
  context.font = font; context.textBaseline = 'top';
  labels.forEach(({ element, position }) => {
    const p = root.localToWorld(position.clone()).project(camera);
    if (p.z < -1 || p.z > 1 || p.x < -1 || p.x > 1 || p.y < -1 || p.y > 1) return;
    const text = element.textContent || '';
    const boxWidth = context.measureText(text).width + 12, boxHeight = 22;
    const x = Math.max(0, Math.min(viewWidth - boxWidth, (p.x + 1) * viewWidth / 2 - boxWidth / 2));
    const y = Math.max(0, Math.min(viewHeight - boxHeight, (1 - p.y) * viewHeight / 2 - boxHeight * 1.3));
    context.fillStyle = '#172033'; context.fillRect(x, y, boxWidth, boxHeight);
    context.fillStyle = '#ffffff'; context.fillText(text, x + 6, y + 3);
  });
  context.restore();
  context.font = font; context.fillStyle = '#ffffff'; context.textBaseline = 'top';
  legendColumns.forEach((lines, i) => lines.forEach((line, row) =>
    context.fillText(line, padding + i * (columnWidth + gap), height + padding + row * lineHeight)));
  return canvas;
}

export function pngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('The browser could not create a PNG image.')), 'image/png');
  });
}
